import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  SessionDetailsRequestData,
  SessionDetailsResponseData,
  SessionInsightsRequestData,
  SessionInsightsResponseData,
} from './session-insights.data-model';

// How long a resolver-fetched session stays reusable by the page.
export const SESSION_PREFETCH_TTL_MS = 10_000;

@Injectable({
  providedIn: 'root',
})
export class SessionInsightsDataService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl = environment.apiUrl;
  private readonly _prefetched = new Map<
    string,
    { at: number; response$: Observable<SessionDetailsResponseData> }
  >();

  getEventDetails(
    requestData: SessionInsightsRequestData
  ): Observable<SessionInsightsResponseData> {
    return this._http.post<SessionInsightsResponseData>(
      this._apiUrl + '/r1/getEventDetails',
      requestData
    );
  }

  getSessionDetails(
    requestData: SessionDetailsRequestData
  ): Observable<SessionDetailsResponseData> {
    // Reuse the resolver's fetch once, so the page does not ask again.
    const key = this._prefetchKey(requestData);
    const hit = this._prefetched.get(key);
    this._prefetched.delete(key);
    if (hit && Date.now() - hit.at < SESSION_PREFETCH_TTL_MS) {
      return hit.response$;
    }
    return this._fetchSessionDetails(requestData);
  }

  prefetchSessionDetails(
    requestData: SessionDetailsRequestData
  ): Observable<SessionDetailsResponseData> {
    const response$ = this._fetchSessionDetails(requestData).pipe(
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this._prefetched.set(this._prefetchKey(requestData), {
      at: Date.now(),
      response$,
    });
    return response$;
  }

  clearPrefetchedSessionDetails(requestData: SessionDetailsRequestData): void {
    this._prefetched.delete(this._prefetchKey(requestData));
  }

  private _fetchSessionDetails(
    requestData: SessionDetailsRequestData
  ): Observable<SessionDetailsResponseData> {
    return this._http.post<SessionDetailsResponseData>(
      this._apiUrl + '/r1/getEventDetails',
      requestData
    );
  }

  private _prefetchKey(requestData: SessionDetailsRequestData): string {
    return `${requestData.event}::${requestData.sessionId}`;
  }
}
